<template>
  <div class="homepage-view">
    <b-container>
      <section v-if="is_authenticated()" class="homepage-view__section">
        <purchase-steps active_id="card" />
      </section>

      <section class="homepage-view__section">
        <hero />
      </section>

      <section class="homepage-view__section">
        <features />
      </section>

      <!--
        senza sessione welfare non c'è credito da convertire: al posto delle
        sezioni che vendono il visitatore trova il gate d'accesso, mentre quelle
        che raccontano il servizio restano per tutti (come booking/bookingv3/
        liveeps fanno col booking engine)
      -->
      <section v-if="!is_authenticated()" class="homepage-view__section">
        <login-gate />
      </section>

      <section v-if="is_authenticated() && !loading" class="homepage-view__section">
        <b-row>
          <b-col
            lg="6"
            class="
              homepage-view__column
              homepage-view__column--first
            "
          >
            <div
              class="
                homepage-view__panel
                homepage-view__panel--product-selection
              "
            >
              <product-selection />
            </div>
          </b-col>
          <b-col
            lg="6"
            class="
              homepage-view__column
              homepage-view__column--second
            "
          >
            <div class="homepage-view__panel">
              <product-detail />
            </div>
            <selected-gift-card />
            <div class="homepage-view__panel">
              <advantages />
            </div>
          </b-col>
        </b-row>
      </section>

      <section class="homepage-view__section">
        <div class="homepage-view__panel">
          <how-it-works />
        </div>
      </section>

      <!-- le condizioni d'acquisto le legge chi può acquistare -->
      <section v-if="is_authenticated()" class="homepage-view__section">
        <div class="homepage-view__panel">
          <conditions />
        </div>
      </section>

      <section v-if="is_authenticated() && !loading" class="homepage-view__section">
        <order-total />
      </section>
    </b-container>
  </div>
</template>

<script>
  import { service_handler } from '@services';
  import { build_products } from '@livegift/resources';
  import PurchaseSteps from '@livegift/components/blocks/purchase-steps.vue';
  import Hero from '@livegift/components/blocks/homepage/hero.vue';
  import Features from '@livegift/components/blocks/homepage/features.vue';
  import LoginGate from '@livegift/components/blocks/homepage/login-gate.vue';
  import ProductSelection from '@livegift/components/blocks/homepage/product-selection.vue';
  import ProductDetail from '@livegift/components/blocks/homepage/product-detail.vue';
  import SelectedGiftCard from '@livegift/components/blocks/gift-card/selected-gift-card.vue';
  import Advantages from '@livegift/components/blocks/homepage/advantages.vue';
  import HowItWorks from '@livegift/components/blocks/homepage/how-it-works.vue';
  import Conditions from '@livegift/components/blocks/homepage/conditions.vue';
  import OrderTotal from '@livegift/components/blocks/homepage/order-total.vue';

  export default {
    name: 'homepage-view',

    components: {
      'purchase-steps': PurchaseSteps,
      'hero': Hero,
      'features': Features,
      'login-gate': LoginGate,
      'product-selection': ProductSelection,
      'product-detail': ProductDetail,
      'selected-gift-card': SelectedGiftCard,
      'advantages': Advantages,
      'how-it-works': HowItWorks,
      'conditions': Conditions,
      'order-total': OrderTotal,
    },

    data() {
      return {
        // il catalogo è l'unica cosa che la home deve caricare: finché non
        // risolve, le sezioni che dipendono dai prodotti non si montano
        loading: true,
      };
    },

    mounted() {
      // senza sessione welfare non c'è niente da comprare: il catalogo
      // alimenterebbe solo sezioni che non si montano, e `products` vuoto è
      // quello su cui conta "Come funziona" per non annunciare un numero di
      // card a chi non può scegliere. NON è una guardia contro un errore: la
      // chiamata riuscirebbe, perché a `gc_catalog.php` basta il token del
      // config, che c'è sempre (vedi src/config.js), non quello di sessione
      if (!this.is_authenticated()) {
        this.loading = false;
        return;
      }

      this.get_catalog();
    },

    methods: {
      /**
       * Catalogo delle gift card: quali card sono in vendita e con quali tagli.
       * È l'unico I/O del flusso, e sta qui perché il site tiene le chiamate nel
       * container di rotta e collega le foglie allo store.
       */
      get_catalog() {
        service_handler('catalog').then(
          (success) => {
            const products = build_products(success.data.giftcardCatalog);

            this.$store.commit('livegift/products', products);

            // il carousel parte dalla prima card: senza un prodotto scelto né la
            // colonna di destra né i tagli avrebbero da cosa derivare
            if (products.length) {
              this.$store.commit('livegift/selected_product', products[0].id);
            }

            this.loading = false;
          },
          (error) => {
            // senza catalogo la home non ha nulla da vendere, e non c'è una
            // pagina precedente su cui rimandare: lascio parlare il modale
            // d'errore del service_handler e le sezioni restano non montate
            this.log(error.response);
            this.loading = false;
          },
        );
      },
    },
  };
</script>

<style lang="scss" scoped>

  .homepage-view {

    // la home è disegnata su un contenuto più stretto (1180px); le altre pagine
    // del site restano sul container globale da 1440px
    .container {

      @include media-breakpoint-up(xl) {
        max-width: 1180px;
      }
    }

    &__section {
      margin-top: $grid-gutter-width;

      @include media-breakpoint-up(md) {
        margin-top: $grid-gutter-width * 2;
      }

      &:last-child {
        margin-bottom: $grid-gutter-width * 3;
      }
    }

    &__panel {
      @include livegift-panel;

      &--product-selection {
        box-shadow: 0 10px 7.5px rgba($black, .1);
      }
    }

    // la colonna destra impila tre riquadri: il distanziamento è di pagina
    &__column {

      & > * + * {
        margin-top: $grid-gutter-20;
      }

      &--first {
        margin-bottom: $grid-gutter-width;

        @include media-breakpoint-up(lg) {
          margin-bottom: 0;
        }
      }

      // la colonna destra segue lo scroll: l'align-self la fa alta quanto il
      // suo contenuto (altrimenti da flex item si stira e lo sticky non ha
      // margine di movimento), poi resta agganciata entro i limiti della riga
      &--second {

        @include media-breakpoint-up(lg) {
          position: sticky;
          top: $grid-gutter-20;
          align-self: flex-start;
        }
      }
    }

    .selected-gift-card {
      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }
</style>